<script lang="ts">
// the open drawers, innermost last: Escape closes only that one
const stack: symbol[] = []
</script>

<script setup lang="ts">
// The side drawer every editor and settings panel opens in: a scrim that
// closes it, a header (title or the header slot, then close), a scrolling
// body and an optional footer. as="form" makes the panel the form, so a
// submit button in the footer submits it; other attributes (@submit …) go
// to the panel. Escape closes it too; with `dirty` (a form with unsaved
// changes) the scrim, Escape and ✕ ask first. Focus moves into the drawer
// and back to what opened it.
import { onMounted, onUnmounted, ref } from 'vue'
import { trapTab } from '../focus'

defineOptions({ inheritAttrs: false })
const props = defineProps<{ title?: string; wide?: boolean; as?: 'div' | 'form'; dirty?: boolean }>()
const emit = defineEmits<{ close: [] }>()
const panel = ref<{ $el?: HTMLElement } | HTMLElement>()
const me = Symbol()
let opener: HTMLElement | null = null

function tryClose() {
    if (props.dirty && !window.confirm('Discard your changes?')) return
    emit('close')
}
function onKey(e: KeyboardEvent) {
    if (e.defaultPrevented || stack.at(-1) !== me) return
    if (e.key === 'Tab') {
        trapTab(e, panel.value instanceof HTMLElement ? panel.value : panel.value?.$el) // Tab stays in the drawer
        return
    }
    if (e.key !== 'Escape') return
    e.preventDefault()
    tryClose()
}
onMounted(() => {
    stack.push(me)
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    window.addEventListener('keydown', onKey)
    const el = panel.value instanceof HTMLElement ? panel.value : panel.value?.$el
    const first = el?.querySelector<HTMLElement>('input:not([type=hidden]), select, textarea')
    ;(first ?? el)?.focus()
})
onUnmounted(() => {
    stack.splice(stack.indexOf(me), 1)
    window.removeEventListener('keydown', onKey)
    opener?.focus()
})
</script>

<template>
    <div class="fixed inset-0 z-20 bg-black/55 scrim" @click.self="tryClose" />
    <component
        :is="as ?? 'div'"
        ref="panel"
        v-bind="$attrs"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        tabindex="-1"
        class="fixed top-0 right-0 bottom-0 z-21 flex max-w-screen slide-in flex-col border-l border-line-strong bg-bg-1 shadow-2xl shadow-black/40 outline-none"
        :class="wide ? 'w-180' : 'w-115'"
    >
        <div class="flex min-h-12 items-center gap-2 border-b border-line chrome px-4 py-2">
            <slot name="header">
                <b class="min-w-0 flex-1 truncate text-sm font-semibold">{{ title }}</b>
            </slot>
            <button type="button" class="btn-ghost" aria-label="Close" @click="tryClose">✕</button>
        </div>
        <div class="flex-1 overflow-auto px-4 py-3"><slot /></div>
        <div v-if="$slots.footer" class="flex justify-end gap-2 border-t border-line bg-bg-2 px-4 py-2.5">
            <slot name="footer" />
        </div>
    </component>
</template>
